import type React from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import {useSubmitButtonHandler} from './SubmitButton/useSubmitButtonHandler';
import styles from './Form.module.css';
import {Message} from '@treatwell/ui/foundations';
import {Text} from '@treatwell/ui';
import {Trans} from 'react-i18next';
import {generateLoginUrl} from '../../../../../shared/utils/login-url';
import {ACCOUNT_ALREADY_EXISTS_ERROR} from './SubmitButton/handleError';
import {getSuccessRedirectPath} from './SubmitButton/getSuccessRedirectPath';

export type FormInputs = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  marketingAllowed: boolean;
};

const FormElement = ({
  children,
}: {
  children: React.ReactElement[] | React.ReactElement;
}) => {
  const {
    formState: {isSubmitSuccessful, errors, isSubmitted},
  } = useFormContext<FormInputs>();
  const {handleSubmit} = useSubmitButtonHandler();
  const serverError = errors?.root?.serverError;
  const serverErrorMessage = serverError?.message;
  const showSubmissionError =
    !isSubmitSuccessful && isSubmitted && serverErrorMessage;

  return (
    <form method="post" data-cy="Form" onSubmit={handleSubmit}>
      {showSubmissionError ? (
        <SubmissionErrorMessage
          serverErrorMessage={serverErrorMessage}
          errorType={serverError?.type}
        />
      ) : null}
      <div className={styles.grid}>{children}</div>
    </form>
  );
};

export const Form = ({
  children,
}: {
  children: React.ReactElement[] | React.ReactElement;
}) => {
  const methods = useForm<FormInputs>();

  return (
    <FormProvider {...methods}>
      <FormElement>{children}</FormElement>
    </FormProvider>
  );
};

const getLoginHref = () => {
  const {hostname, search, pathname} = window.location;
  const isFinalizingOrder = new URLSearchParams(search).has('finalizeOrder');
  const route = isFinalizingOrder
    ? encodeURIComponent(getSuccessRedirectPath(search, pathname))
    : undefined;
  return generateLoginUrl(hostname, route);
};

const SubmissionErrorMessage = ({
  serverErrorMessage,
  errorType,
}: {
  serverErrorMessage: string;
  errorType?: string | number;
}) => {
  return (
    <div className={styles.submissionError}>
      <Message type="error">
        <Text type="caption">
          {errorType === ACCOUNT_ALREADY_EXISTS_ERROR ? (
            <Trans
              i18nKey="register.errors.accountAlreadyExists"
              components={[
                <a
                  key="login"
                  href={getLoginHref()}
                  className={styles.errorLink}
                  data-cy="RegisterErrorLoginLink"
                />,
              ]}
            />
          ) : (
            serverErrorMessage
          )}
        </Text>
      </Message>
    </div>
  );
};
